/**
 * Tour script builder for the Matcher workspace. Each step targets a CSS
 * selector for a real UI element; `target: null` is rendered as a centered
 * intro/outro card (no spotlight). Steps with missing targets are skipped
 * automatically.
 *
 * Built as a function (not a const) so the persona name is woven into copy
 * dynamically — switching from Job to Jasmijn updates the tour without a
 * second copy of the script.
 */
export function buildTourSteps(persona) {
  const name = persona?.name || 'Matcher';

  return [
    {
      target: null,
      title: 'Welkom bij de Matcher!',
      text: `Ik ben ${name}, je digitale vacaturecoach. Laat me je in 60 seconden de werkruimte uitleggen. Je kunt altijd overslaan met Escape.`,
    },
    {
      target: '.ws-header__avatar-wrap',
      title: `Dit ben ik: ${name}`,
      text: 'Mijn avatar laat zien wanneer ik luister of antwoord. Boven mij zie je jouw voortgang: hoeveel secties van de vacature al klaar zijn.',
      position: 'bottom',
    },
    {
      target: '.voice-orb',
      title: 'Spraak: praat met mij',
      text: 'Klik op de microfoon om een gesprek te starten. Ik stel vragen, jij antwoordt mondeling, en de vacature vult zich vanzelf in. Ideaal als je liever vertelt dan typt.',
      position: 'top',
    },
    {
      target: '.input-bar__dictate',
      title: 'Dicteren: vertel je verhaal',
      text: 'Wil je gewoon iets inspreken zonder dat ik tussendoor vragen stel? Klik op deze knop en je woorden komen direct in het tekstvak terecht.',
      position: 'top',
    },
    {
      target: '.input-bar__text-wrap',
      title: 'Typen: gewoon schrijven',
      text: 'Liever typen? Doe het hier. Druk op Enter om te versturen, Shift+Enter voor een nieuwe regel.',
      position: 'top',
    },
    {
      target: '.input-bar__upload',
      title: 'Bestand uploaden',
      text: 'Heb je al een bestaande vacaturetekst? Upload een PDF, Word of TXT-bestand, en ik herschrijf hem naar een sterke versie.',
      position: 'top',
    },
    {
      target: '.document-panel__header',
      title: 'Je vacature groeit hier',
      text: 'Aan de rechterkant zie je de samenvatting van je vacature opbouwen terwijl we praten. Je kunt deze op elk moment kopiëren of downloaden.',
      position: 'left',
    },
    {
      target: '.doc-section',
      title: 'Bewerken per sectie',
      text: 'Klik op elke sectie om de tekst direct te bewerken. Gebruik de iconen om te herschrijven (🔄) of in te korten (✂️). Handig om snel varianten te zien.',
      position: 'left',
    },
    {
      target: '.ws-header__action[aria-label="Persoonlijke voorkeuren"]',
      title: 'Persoonlijke voorkeuren',
      text: `Stel hier in welke toon, stijl en branche bij jullie organisatie past. Hier wissel je ook van stem (${name} ↔ andere persona). Ik onthoud dit zodat élke vacature meteen klopt.`,
      position: 'bottom',
    },
    {
      target: '.ws-header__action[aria-label="Begin opnieuw"]',
      title: 'Begin opnieuw',
      text: 'Klaar met een vacature? Druk hier om met een nieuwe te beginnen. Je voorkeuren blijven bewaard.',
      position: 'bottom',
    },
    {
      target: null,
      title: 'Veel succes!',
      text: 'Dat was alles. Druk op het vraagteken (?) in de header om deze rondleiding opnieuw te zien. Aan de slag!',
    },
  ];
}

/** Kept for backwards compatibility — uses default persona name. */
export const WORKSPACE_TOUR_STEPS = buildTourSteps({ name: 'Matcher' });

export const TOUR_SEEN_KEY = 'hai-job.tour.completed.v1';
